import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search, ArrowUpDown } from "lucide-react";

const SalesPanel = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get logged-in seller ID
  const sellerId = (() => {
    const userStr = localStorage.getItem("erpUser") || localStorage.getItem("user");
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    return user.userid || user.id || null;
  })();

  useEffect(() => {
    if (!sellerId) {
      setError("Anda harus login sebagai penjual untuk melihat panel ini");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch only orders for this seller
        const { data } = await axios.get("https://api.kamulyaan.id/order", {
          params: { store_id: sellerId },
        });

        const orderData = Array.isArray(data) ? data : data?.data || data?.orders || [];
        setOrders(orderData);
      } catch (err) {
        console.error("Failed to load sales:", err);
        setError("Gagal memuat daftar penjualan");
      } finally {
        setLoading(false);
      }
    })();
  }, [sellerId]);

  // Filter
  const filtered = orders.filter((o) =>
    [
      o.id,
      o.title,
      o.buyer_id,
      o.store_id,
      o.product_id,
      o.status,
      o.total_amount,
    ].some((v) => String(v ?? "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === "total_amount" || sortConfig.key === "quantity") {
      aVal = Number(aVal ?? 0);
      bVal = Number(bVal ?? 0);
    }

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIcon = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc" ? "↑" : "↓"
      : <ArrowUpDown size={14} />;

  if (loading) return <div className="loading">Memuat penjualan...</div>;
  if (error)   return <div className="error">{error}</div>;
  if (!sellerId) return <div className="error">Silakan login sebagai penjual</div>;

  return (
    <div className="admin-page p-4 max-w-full">
      <header className="mb-4">
        <h1 className="text-2xl font-bold mb-3">Sales Panel – Pesanan Masuk</h1>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] flex items-center bg-white border border-gray-300 rounded-full px-3 py-2">
            <Search size={18} className="text-gray-500" />
            <input
              className="flex-1 ml-2 outline-none bg-transparent"
              placeholder="Cari ID, produk, buyer, status, total..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white max-h-[70vh]">
        <table className="w-full min-w-[1200px] border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {[
                { label: "ID",        key: "id"          },
                { label: "Produk",    key: "title"       },
                { label: "Buyer ID",  key: "buyer_id"    },
                { label: "Seller ID", key: "store_id"    },
                { label: "Produk ID", key: "product_id"  },
                { label: "Jumlah",    key: "quantity"    },
                { label: "Total",     key: "total_amount", className: "text-right" },
                { label: "Status",    key: "status"      },
                { label: "Tanggal",   key: "order_date"  },
              ].map(({ label, key, className = "" }) => (
                <th
                  key={label}
                  className={`p-3 font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-200 ${className} text-left`}
                  onClick={() => key && toggleSort(key)}
                >
                  {label} {key && sortIcon(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-16 text-gray-500">
                  Belum ada pesanan masuk
                </td>
              </tr>
            ) : (
              sorted.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="p-3 font-medium">{order.id}</td>
                  <td className="p-3 max-w-xs truncate">{order.title || "—"}</td>
                  <td className="p-3">{order.buyer_id || "—"}</td>
                  <td className="p-3">{order.store_id || "—"}</td>
                  <td className="p-3">{order.product_id || "—"}</td>
                  <td className="p-3 text-center">{order.quantity || 0}</td>
                  <td className="p-3 font-medium text-green-700 text-right">
                    Rp {Number(order.total_amount ?? 0).toLocaleString("id-ID")}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block px-3 py-1 text-xs rounded-full ${
                        order.status === "Belum bayar"
                          ? "bg-yellow-100 text-yellow-800"
                          : order.status === "Sudah bayar"
                          ? "bg-green-100 text-green-800"
                          : order.status === "Processing"
                          ? "bg-blue-100 text-blue-800"
                          : order.status === "Sudah dikirim"
                          ? "bg-purple-100 text-purple-800"
                          : order.status === "Paket diterima" || order.status === "Selesai"
                          ? "bg-emerald-100 text-emerald-800"
                          : order.status === "Batal"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {order.status || "—"}
                    </span>
                  </td>
                  <td className="p-3">{order.order_date?.split("T")[0] || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesPanel;